/**
 * Format a large number into a more readable string
 * Examples:
 * - 247 → "247"
 * - 1,234 → "1.2k"
 * - 15,000 → "15k"
 * - 1,234,567 → "1.2M"
 */
export function formatCount(count: number): string {
  if (count < 1000) {
    return count.toString();
  }

  if (count < 1000000) {
    const thousands = count / 1000;
    // Show one decimal place if it's not a whole number
    return thousands % 1 === 0
      ? `${thousands}k`
      : `${thousands.toFixed(1)}k`;
  }

  const millions = count / 1000000;
  return millions % 1 === 0
    ? `${millions}M`
    : `${millions.toFixed(1)}M`;
}

/**
 * Format a token count the way the model catalog states context windows and
 * output limits (task 1074): uppercase K/M, at most one decimal.
 * Examples: 200000 → "200K", 16384 → "16.4K", 1047576 → "1M", 512 → "512".
 */
export function formatTokenCount(tokens: number): string {
  const compact = (value: number, suffix: string) => {
    const rounded = Math.round(value * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}${suffix}`;
  };
  if (tokens < 1000) return tokens.toLocaleString('en-US');
  if (tokens < 1000000) return compact(tokens / 1000, 'K');
  return compact(tokens / 1000000, 'M');
}

/**
 * Format a USD-per-million-tokens price. The service serializes the catalog's
 * Decimal price columns as strings ("3", "0.15"), so this accepts either and
 * renders an em-dash for a missing or unparseable value.
 */
export function formatPricePerMTok(value: number | string | null | undefined): string {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return `$${parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
}

/**
 * The Prompt caching row a Model Config's detail card and the agent Model &
 * runtime panel show (task 1126): "On · 5-minute cache" or "Off" when the
 * Model caches explicitly (Anthropic), "Automatic" when the provider caches on
 * its own (OpenAI), and null — omit the row — when caching is not available.
 */
export function promptCachingLabel(config: {
  promptCaching: boolean;
  model: { promptCaching: 'explicit' | 'automatic' | 'none' };
}): string | null {
  switch (config.model.promptCaching) {
    case 'explicit':
      return config.promptCaching ? 'On · 5-minute cache' : 'Off';
    case 'automatic':
      return 'Automatic';
    default:
      return null;
  }
}

/**
 * A run's cost line (task 1253): "4.8K tokens · $0.0137". Dollars show two
 * decimals from one cent up and four below it, so a sub-cent run does not read
 * as free; exactly zero reads "$0.00".
 */
export function formatRunCost(cost: { totalTokens: number; estimatedCostUsd: number }): string {
  const usd = cost.estimatedCostUsd;
  const digits = usd > 0 && usd < 0.01 ? 4 : 2;
  const dollars = `$${usd.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  return `${formatTokenCount(cost.totalTokens)} tokens · ${dollars}`;
}

/**
 * The agents list's Used by value (task 1311): "N services", or an em-dash when
 * no collaboration config references the agent (or the count is unknown).
 */
export const usedByLabel = (count: number | undefined): string =>
  !count ? '—' : `${count} ${count === 1 ? 'service' : 'services'}`;
